import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * UPRAWNIENIA DO PRODUKTÓW — dostęp (`Restrictions2`) i limit (`Rationing`) w jednym widoku.
 *
 * ⚠️ TE DWIE TABELE SPS NIE MAJĄ WSPÓLNEGO IDENTYFIKATORA — łączy je wyłącznie para (kto, co).
 * Całe tłumaczenie masek robi API (`api/src/permissions/maska.ts`); front dostaje gotowe
 * rodzaje (`employee` / `group` / `all`) i nie ma prawa ich rekonstruować z cyfr.
 */

export const StanFlagiSchema = z.enum(['WLACZONE', 'WYLACZONE', 'ROZNI_SIE', 'NIEUSTALONE']);
export type StanFlagi = z.infer<typeof StanFlagiSchema>;

export const FlagaSchema = z.object({
  param: z.string(),
  stan: StanFlagiSchema,
  /** Opis WPROST z bazy klienta — jedyna dokumentacja tych flag, jaką mamy. */
  opis: z.string().nullable(),
  wlaczonych: z.number(),
  automatow: z.number(),
  /** Wypełniony tylko przy `NIEUSTALONE` — bez niego „nieustalone" nie mówi, czy czekać. */
  powod: z.string().nullable(),
  per: z.array(z.object({ vmid: z.number(), nazwa: z.string().nullable(), wlaczone: z.boolean().nullable() })),
});
export type Flaga = z.infer<typeof FlagaSchema>;

export const KonfiguracjaSchema = z.object({
  automatow: z.number(),
  /**
   * ⚠️ `false` NIE ZNACZY „nikt nie ma dostępu". Znaczy „nie wolno twierdzić, że wpis nadaje
   * dostęp" — bo albo automat listy nie czyta (`REST_ENABLED=0`), albo lista ZABIERA zamiast
   * nadawać (`REST_DENY=1`), albo po prostu jeszcze nie wiemy.
   */
  dostepNadaje: z.boolean(),
  flagi: z.record(z.string(), FlagaSchema),
});
export type Konfiguracja = z.infer<typeof KonfiguracjaSchema>;

export const AdresatSchema = z.object({
  kind: z.enum(['employee', 'group', 'all']),
  id: z.string().nullable(),
  name: z.string(),
  spsKey: z.string(),
  /** Ilu pracowników obejmuje. `null` dla „wszystkich" — liczby nie znamy i nie zgadujemy. */
  osob: z.number().nullable(),
});
export type Adresat = z.infer<typeof AdresatSchema>;

export const ZakresSchema = z.object({
  kind: z.enum(['item', 'itemGroup', 'itemSubGroup', 'all']),
  id: z.string().nullable(),
  name: z.string(),
  spsKey: z.string(),
});
export type Zakres = z.infer<typeof ZakresSchema>;

export const LimitSchema = z.object({
  id: z.string(),
  maxQty: z.number(),
  period: z.number(),
  periodType: z.number(),
  periodLabel: z.string(),
  /** `true` = SPSUI nie egzekwuje tego okresu (miesiąc, rok) — limit istnieje, ale nie działa. */
  unenforced: z.boolean(),
});
export type Limit = z.infer<typeof LimitSchema>;

export const UprawnienieSchema = z.object({
  id: z.string(),
  zakres: ZakresSchema,
  limit: LimitSchema.nullable(),
  /**
   * Gdzie mieszka limit tego wiersza; `null` = nie ma gdzie go zapisać (zakres „wszystkie").
   *
   * ⚠️⚠️ `zNadgrupy` ZNACZY, ŻE ZAPIS SIĘGA SZERZEJ NIŻ WIERSZ. Dostęp na podgrupie towarowej
   * nie ma odpowiednika w `Rationing` (`ITEMTYPE` zna towar i grupę główną), więc limit siedzi
   * na grupie NADRZĘDNEJ i obejmuje także towary spoza tego uprawnienia.
   */
  zakresLimitu: z
    .object({
      rodzaj: z.enum(['item', 'itemGroup']),
      nazwa: z.string(),
      spsKey: z.string(),
      zNadgrupy: z.boolean(),
    })
    .nullable(),
  /**
   * ⚠️⚠️ NIEPUSTE = TO NIE JEST WIERSZ DOSTĘPU, A SAMA REGUŁA LIMITU, i `id` jest wtedy
   * identyfikatorem REGUŁY. Zapis idzie trasami `/rationing/:id`, nie `/permissions/:id/limit`;
   * „Odbierz dostęp" nie ma tu sensu, bo dostęp idzie z innego wiersza (opisanego w `przez`).
   */
  bezDostepu: z
    .object({
      /** `null` = nie znaleźliśmy żadnego wpisu dostępu obejmującego ten limit. */
      przez: z.string().nullable(),
      /** Czego konkretnie nie widzimy — puste, gdy dostęp jest. */
      coWiemy: z.string(),
    })
    .nullable(),
  /** Niepuste = wiersz zawężony przez zadanie/automat, więc TYLKO ODCZYT. */
  zawezone: z.array(z.string()),
  pending: z.boolean(),
  /** Ile razy PORTAL ruszał limit tej pary — zero znaczy „nie ruszał", nie „nie wiem". */
  zmianPortalu: z.number(),
});
export type Uprawnienie = z.infer<typeof UprawnienieSchema>;

const PermissionsSchema = z.object({
  konfiguracja: KonfiguracjaSchema,
  grupy: z.array(z.object({ adresat: AdresatSchema, uprawnienia: z.array(UprawnienieSchema) })),
  widocznych: z.number(),
  wszystkich: z.number(),
  zLimitem: z.number(),
  bezLimitu: z.number(),
  /** Która strona adresatów i ilu ich jest po filtrach — tylko przy zapytaniu ze stronami. */
  strona: z.object({ page: z.number(), pageSize: z.number(), adresatow: z.number() }).optional(),
  /** Wiersze, których maski nie rozumiemy — pokazywane, nie pomijane. */
  nierozpoznane: z.array(
    z.object({
      id: z.string(),
      dConsumer: z.string(),
      dItem: z.string(),
      rUser: z.string(),
      rItem: z.string(),
      powod: z.string(),
    }),
  ),
  /**
   * ═════════════════════════════════════════════════════════════════
   *  REGUŁY LIMITU BEZ WŁASNEGO WIERSZA — nie wszystkie są martwe.
   *
   *  ⚠️⚠️ `martwy` DOŁOŻONE 07.09.2026, bo poprzednia wersja OGŁASZAŁA MARTWĄ każdą z nich —
   *  a reguła ze sprawy EMCFIME (limit imienny na grupie, dostęp grupowy na jej podgrupie)
   *  blokowała człowieka na 11 towarów do 28.07.2027. `false` znaczy „działa, tylko innym wpisem
   *  dostępu", a `powod` mówi którym i gdzie ją edytować.
   * ═════════════════════════════════════════════════════════════════
   */
  limityBezDostepu: z.array(
    z.object({
      id: z.string(),
      cel: z.string(),
      zakres: z.string(),
      powod: z.string(),
      martwy: z.boolean(),
      celKind: z.enum(['employee', 'group']).nullable(),
    }),
  ),
});

export type PermissionsPage = z.infer<typeof PermissionsSchema>;

export type ListPermissionsParams = {
  groupId: string;
  search?: string;
  target: 'all' | 'employees' | 'groups';
  mode: 'all' | 'nolimit' | 'limit';
  /** Strona ADRESATÓW (osoba albo grupa z kompletem uprawnień), od 1 — 14.10.2026, Adient. */
  page?: number;
  pageSize?: number;
};

export const listPermissions = (p: ListPermissionsParams) => {
  const q = new URLSearchParams({ groupId: p.groupId, target: p.target, mode: p.mode });
  if (p.search) q.set('search', p.search);
  if (p.page !== undefined) q.set('page', String(p.page));
  if (p.pageSize !== undefined) q.set('pageSize', String(p.pageSize));
  return apiGet(`/permissions?${q.toString()}`, PermissionsSchema);
};

/** Flagi konfiguracji BEZ listy uprawnień — dla ekranów, które pytają tylko „czy to działa". */
export const fetchKonfiguracjaUprawnien = (groupId: string) =>
  apiGet(`/permissions/konfiguracja?groupId=${groupId}`, KonfiguracjaSchema);

// ── ZAPIS ────────────────────────────────────────────────────────────────────────────────────

const OpSchema = z.object({ opId: z.string() });

export const PermissionOptionsSchema = z.object({
  employeeGroups: z.array(
    z.object({ id: z.string(), spsGroupNumber: z.string(), name: z.string(), members: z.number() }),
  ),
  itemGroups: z.array(z.object({ id: z.string(), code: z.string(), name: z.string(), items: z.number() })),
  employees: z.array(
    z.object({ id: z.string(), spsUserNumber: z.string(), name: z.string(), rationEffect: z.number() }),
  ),
  items: z.array(z.object({ id: z.string(), itemNumber: z.string(), name: z.string() })),
});
export type PermissionOptions = z.infer<typeof PermissionOptionsSchema>;

export const fetchPermissionOptions = (groupId: string, search?: string) => {
  const q = new URLSearchParams({ groupId });
  if (search) q.set('search', search);
  return apiGet(`/permissions/opcje?${q.toString()}`, PermissionOptionsSchema);
};

export type Okno = { maxQty: number; period: number; periodType: number };

/**
 * Nadanie dostępu; `limit` podany = kreator, czyli dostęp i limit w JEDNEJ operacji.
 *
 * ⚠️ Adresat i zakres jadą NASZYMI identyfikatorami, nie kluczami SPS — klucz naturalny serwer
 * odczytuje sam. Wysyłanie `USERNUMBER` z przeglądarki znaczyłoby, że front może zaadresować
 * dowolny wiersz w bazie klienta.
 */
/**
 * ZDERZENIE LIMITU Z PEŁNYM WYDANIEM — kto NIE POBIERZE, choć ma dostęp.
 *
 * ⚠️ Trzy liczby z trzech tabel, których nikt nie widzi razem: reguła „pełne wydanie" na szufladzie,
 * ilość maksymalna przegródki i limit z wydzielania. Reguła i uzasadnienie stoją w instancji
 * (`zderzenia-pelnego-wydania.ts`) — ekran ich NIE liczy, żeby nie było dwóch kopii.
 */
export const MiejscePelnegoWydaniaSchema = z.object({
  itemNumber: z.string(),
  itemNazwa: z.string(),
  itemGroup: z.string().nullable(),
  /** Ile wychodzi za JEDNYM pobraniem. */
  wydanie: z.number(),
  automat: z.object({ vmid: z.number(), nazwa: z.string() }),
  picNumber: z.number(),
  drawerNo: z.number(),
  compartmentNo: z.number(),
});

/**
 * ⚠️⚠️ POLA PO POLSKU (`rodzaj`, `nazwa`), BO TAK NAZYWA JE INSTANCJA — i to jest POPRAWKA PO
 * ZMIERZONEJ USTERCE (31.08.2026), nie kwestia gustu.
 *
 * Ten schemat opisywał adresata jako `{ kind, name }` (przez `AdresatSchema.pick`, gdzie tak się
 * nazywają), a `zderzenia-pelnego-wydania.ts` oddaje `{ rodzaj, nazwa }`. Zod odrzucał więc CAŁĄ
 * odpowiedź, `useQuery` wchodził w `isError`, a panel na Uprawnieniach zwracał `null` — czyli
 * blokada limitu, którą instancja PRAWIDŁOWO wykryła (i o której wystawiła alert), na ekranie
 * uprawnień nie istniała. Zgłoszone jako „nigdzie nie widzę tego błędu".
 *
 * ⚠️ ANI `tsc`, ANI TESTY TEGO NIE ŁAPAŁY: komponent typuje się z `z.infer` TEGO schematu, więc
 * kompilował się zgodnie z nim, a nie z rzeczywistością. Szew klient↔instancja pilnuje teraz
 * `zderzenia-kontrakt.spec.ts`, który buduje wynik PRAWDZIWĄ funkcją instancji i parsuje go tym
 * schematem.
 */
export const ZderzenieSchema = z.object({
  limit: z.object({
    /** Id wiersza UPRAWNIENIA — tym samym woła się `setPermissionLimit`, więc naprawa jest jednym klikiem. */
    uprawnienieId: z.string(),
    adresat: z.object({
      rodzaj: z.enum(['employee', 'group', 'all']),
      nazwa: z.string(),
      spsKey: z.string(),
      /** Ilu pracowników obejmuje. `null` dla „wszystkich" — liczby nie znamy i nie zgadujemy. */
      osob: z.number().nullable(),
    }),
    zakres: z.object({
      rodzaj: z.enum(['item', 'itemGroup', 'itemSubGroup', 'all']),
      nazwa: z.string(),
      spsKey: z.string(),
    }),
    maxQty: z.number(),
    period: z.number(),
    periodType: z.number(),
    periodLabel: z.string(),
    /** `true` = wiersz zawężony w SPS, więc w portalu tylko do odczytu — naprawa musi iść przez SPSUI. */
    tylkoOdczyt: z.boolean(),
  }),
  miejsca: z.array(MiejscePelnegoWydaniaSchema),
  /** Ile musiałby wynieść limit, żeby cokolwiek wyszło. */
  najmniejszyDzialajacyLimit: z.number(),
});
export type Zderzenie = z.infer<typeof ZderzenieSchema>;

export const ZderzeniaSchema = z.object({
  zderzenia: z.array(ZderzenieSchema),
  miejscPelnegoWydania: z.number(),
  limitow: z.number(),
  /** Limity, których czujnik NIE UMIE sprawdzić — musi być widoczne, nie pomijane po cichu. */
  niesprawdzone: z.array(z.object({ cel: z.string(), zakres: z.string(), powod: z.string() })),
});
export type ZderzeniaPage = z.infer<typeof ZderzeniaSchema>;

export const fetchZderzenia = (groupId: string) =>
  apiGet(`/permissions/zderzenia?groupId=${encodeURIComponent(groupId)}`, ZderzeniaSchema);

export const grantPermission = (p: {
  groupId: string;
  employeeId?: string;
  employeeGroupId?: string;
  /** „Wszyscy pracownicy" — tylko bez limitu (SPS nie zapisze limitu na „wszystkich"). */
  allEmployees?: true;
  itemId?: string;
  itemGroupId?: string;
  /** „Wszystkie produkty" — tylko bez limitu. */
  allItems?: true;
  limit?: Okno;
}) => apiSend('POST', '/permissions', p, z.object({ opId: z.string(), id: z.string() }));

/** Ustawienie ALBO zmiana limitu — jeden adres, bo skutek jest ten sam niezależnie od stanu przed. */
export const setPermissionLimit = (id: string, groupId: string, okno: Okno) =>
  apiSend('PUT', `/permissions/${id}/limit`, { groupId, ...okno }, OpSchema);

/** „Przypisz Nolimit" — zdejmuje limit, ZOSTAWIA dostęp. */
export const removePermissionLimit = (id: string, groupId: string) =>
  apiSend('DELETE', `/permissions/${id}/limit?groupId=${groupId}`, undefined, OpSchema);

/**
 * HISTORIA ZMIAN LIMITU ZŁOŻONYCH PRZEZ PORTAL.
 *
 * ⚠️ ZMIAN ZROBIONYCH PRZY MASZYNIE (SPSUtilities) TU NIE MA i ekran musi tak to nazywać —
 * portal ich nie składał, więc nie ma o nich wiersza. Uzasadnienie stoi w `historia-limitu.ts`.
 */
export const WpisHistoriiLimituSchema = z.object({
  id: z.string(),
  kiedy: z.string(),
  rodzaj: z.enum(['USTAWIONY', 'ZMIENIONY', 'ZDJETY', 'INNE']),
  /** `null` przy zdjęciu limitu — po tej zmianie limitu nie ma. */
  maxQty: z.number().nullable(),
  okno: z.string().nullable(),
  kto: z.string().nullable(),
  tytul: z.string(),
  stan: z.string(),
  potwierdzono: z.string().nullable(),
});
export type WpisHistoriiLimitu = z.infer<typeof WpisHistoriiLimituSchema>;

export const fetchHistoriaLimitu = (id: string, groupId: string) =>
  apiGet(
    `/permissions/${id}/historia-limitu?groupId=${encodeURIComponent(groupId)}`,
    z.object({ wpisy: z.array(WpisHistoriiLimituSchema) }),
  );

/** „Odbierz dostęp" — usuwa wpis dostępu, a razem z nim limit. */
export const revokePermission = (id: string, groupId: string) =>
  apiSend('DELETE', `/permissions/${id}?groupId=${groupId}`, undefined, OpSchema);

/** Ile osób zostanie bez ŻADNEGO wpisu dostępu po odebraniu tego jednego — do pytania o potwierdzenie. */
export const fetchSkutekOdebrania = (id: string, groupId: string) =>
  apiGet(
    `/permissions/${id}/skutek-odebrania?groupId=${encodeURIComponent(groupId)}`,
    z.object({ wszystkich: z.number(), bezDostepu: z.number() }),
  );

/** Jednostki okna limitu — te same numery co `Rationing.PERIODTYPE`. */
export const JEDNOSTKI_OKNA = [
  { v: 1, l: 'minut' },
  { v: 2, l: 'godzin' },
  { v: 3, l: 'dni' },
  { v: 4, l: 'tygodni' },
  /**
   * ⚠️ 5 i 6 SĄ W SPS, ALE AUTOMAT ICH NIE EGZEKWUJE (SPSUI obsługuje tylko 1-4). Zostawiamy je
   * do wyboru, bo takie reguły u klientów istnieją i trzeba je umieć poprawić — ale formularz
   * musi powiedzieć, że taki limit nie zadziała.
   */
  { v: 5, l: 'miesięcy (automat tego nie pilnuje)' },
  { v: 6, l: 'lat (automat tego nie pilnuje)' },
] as const;

/** Etykiety rodzajów zakresu — jedno miejsce, bo pojawiają się przy każdym wierszu. */
export const ETYKIETA_ZAKRESU: Record<Zakres['kind'], string> = {
  item: 'produkt',
  itemGroup: 'grupa produktów',
  itemSubGroup: 'podgrupa produktów',
  all: 'wszystkie produkty',
};

export const ETYKIETA_ADRESATA: Record<Adresat['kind'], string> = {
  employee: 'Pracownik',
  group: 'Grupa pracowników',
  all: 'Wszyscy pracownicy',
};
